import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { ArrowRight, ArrowLeft, Calendar, Tag } from 'lucide-react';
import axios from 'axios';
import Breadcrumbs from '../components/layout/Breadcrumbs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPost() {
  const { slug } = useParams();
  const { language, t } = useLanguage();
  const basePath = language === 'en' ? '/en' : '';
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API}/blog/${slug}`);
      setPost(response.data);
    } catch (error) {
      console.error('Error fetching blog post:', error);
      // Placeholder post
      setPost({
        id: '1',
        slug: slug,
        title_nl: 'SEO Artikel',
        title_en: 'SEO Article',
        content_nl: '<p>Dit artikel wordt binnenkort gepubliceerd.</p>',
        content_en: '<p>This article will be published soon.</p>',
        category: 'SEO',
        image_url: 'https://images.unsplash.com/photo-1702315139575-ba243e9602a0?crop=entropy&cs=srgb&fm=jpg&q=85',
        created_at: new Date().toISOString()
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'nl' ? 'nl-NL' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="section-spacing">
        <div className="container-kesult max-w-3xl">
          <div className="animate-pulse space-y-8">
            <div className="h-8 bg-gray-200 w-3/4" />
            <div className="aspect-video bg-gray-200" />
            <div className="space-y-4">
              <div className="h-4 bg-gray-200 w-full" />
              <div className="h-4 bg-gray-200 w-5/6" />
              <div className="h-4 bg-gray-200 w-4/5" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="section-spacing">
        <div className="container-kesult text-center">
          <h1 className="heading-2 mb-4">{language === 'nl' ? 'Artikel niet gevonden' : 'Article not found'}</h1>
          <Link to={`${basePath}/blog`}>
            <Button className="btn-primary">
              <ArrowLeft className="mr-2 h-5 w-5" />
              {t('blog.back')}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={`blog-post-${slug}`}>
      <Breadcrumbs />
      {/* Article */}
      <article className="section-spacing bg-white">
        <div className="container-kesult max-w-3xl">
          <Link 
            to={`${basePath}/blog`}
            className="inline-flex items-center gap-2 mb-8 text-[#71717A] hover:text-black"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('blog.back')}
          </Link>

          <header className="mb-12">
            <div className="flex items-center gap-4 mb-6">
              <span className="badge-kesult">{post.category}</span>
              <span className="text-sm text-[#71717A] flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {formatDate(post.created_at)}
              </span>
            </div>
            <h1 className="heading-1 mb-6" data-testid="post-title">
              {language === 'nl' ? post.title_nl : post.title_en}
            </h1>
          </header>

          {post.image_url && (
            <div className="mb-12">
              <img 
                src={post.image_url}
                alt={language === 'nl' ? post.title_nl : post.title_en}
                className="w-full aspect-video object-cover border-2 border-[#F1F0EF] rounded-[2rem] shadow-xl"
              />
            </div>
          )}

          <div 
            className="prose prose-lg max-w-none"
            dangerouslySetInnerHTML={{ 
              __html: language === 'nl' ? post.content_nl : post.content_en 
            }}
          />
        </div>
      </article>

      {/* CTA */}
      <section className="section-spacing bg-[#F1F0EF]">
        <div className="container-kesult text-center">
          <h2 className="heading-2 mb-6">
            {language === 'nl' ? 'WIL JE JE SEO VERBETEREN?' : 'WANT TO IMPROVE YOUR SEO?'}
          </h2>
          <p className="body-large text-[#71717A] max-w-2xl mx-auto mb-10">
            {t('quickScan.subtitle')}
          </p>
          <Link to={`${basePath}/contact`}>
            <Button className="btn-primary">
              {language === 'nl' ? 'Neem contact op' : 'Get in touch'}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
